import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import { handleUnauthorized } from '../services/api';
import { getCache, setCache, CACHE_KEYS } from '../services/cacheService';

interface DocumentCategory {
  id: string;
  name: string;
  description?: string;
}

export const useDocumentCategories = () => {
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async (forceRefresh = false) => {
    // Check cache first unless force refresh is requested
    if (!forceRefresh) {
      const cached = getCache<DocumentCategory[]>(CACHE_KEYS.CATEGORIES);
      if (cached) {
        setCategories(cached);
        return cached;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`${API_BASE_URL}/document-categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) { handleUnauthorized(); return []; }
        throw new Error('Failed to fetch document categories');
      }

      const data = await response.json();
      
      // Backend returns categories in format: {"categories": [...]}
      let extractedCategories: DocumentCategory[] = [];
      
      if (data && data.categories && Array.isArray(data.categories)) {
        extractedCategories = data.categories;
      } else if (Array.isArray(data)) {
        extractedCategories = data;
      } else {
        console.error('Unexpected API response structure for categories:', data);
      }
      
      setCategories(extractedCategories);
      setCache(CACHE_KEYS.CATEGORIES, extractedCategories);
      
      return extractedCategories;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      console.error('Error fetching document categories:', errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Load categories on first mount
  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    loading,
    error,
    fetchCategories,
  };
};
